import React from 'react';
import { styled } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ACCENT_BLUE } from './qcTheme';
import { useProfileVersion } from '../../Vendors/profileVersion';

// Sticky at the scrollport bottom (prototype). Under the v2 shell the main box is the scrollport, so `bottom` compensates its 24px padding; under the legacy shell the window scrolls and that offset would clip the bar below the viewport.
const FooterBar = styled('div', {
  shouldForwardProp: prop => prop !== 'v2Shell',
})<{ v2Shell: boolean }>(({ v2Shell }) => ({
  position: 'sticky',
  bottom: v2Shell ? '-24px' : 0,
  zIndex: 6,
  marginTop: 'auto',
  display: 'flex',
  alignItems: 'stretch',
  borderTop: '1px solid rgba(0,0,0,0.08)',
  background: '#fff',
  borderRadius: '0 0 4px 4px',
  boxShadow: '0 -2px 6px rgba(0,0,0,0.06)',
}));

// Each link fills its half of the bar, so the whole half is the click target, not just the label.
const PagerLink = styled('button', {
  shouldForwardProp: prop => prop !== 'on' && prop !== 'align',
})<{ on: boolean; align: 'start' | 'end' }>(({ on, align }) => ({
  display: 'flex',
  flexGrow: 1,
  flexBasis: 0,
  justifyContent: align === 'start' ? 'flex-start' : 'flex-end',
  alignItems: 'center',
  gap: '4px',
  border: 'none',
  borderRadius: align === 'start' ? '0 0 0 4px' : '0 0 4px 0',
  background: 'none',
  padding: '16px 24px',
  transition: 'background-color 150ms ease',
  '&:not(:disabled):hover': {
    backgroundColor: 'rgba(25,118,210,0.04)',
    '@media (hover: none)': { backgroundColor: 'transparent' },
  },
  fontFamily: 'inherit',
  fontSize: '0.8125rem',
  fontWeight: 500,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  color: on ? ACCENT_BLUE : 'rgba(0,0,0,0.38)',
  cursor: on ? 'pointer' : 'default',
  userSelect: 'none',
  '& .MuiSvgIcon-root': {
    fontSize: '18px',
  },
}));

export interface QuestionPagerFooterProps {
  hasPrevious: boolean;
  hasNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
}

export default function QuestionPagerFooter({ hasPrevious, hasNext, onPrevious, onNext }: QuestionPagerFooterProps): JSX.Element {
  const { showV2 } = useProfileVersion();
  return (
    <FooterBar v2Shell={showV2}>
      <PagerLink type='button' align='start' on={hasPrevious} disabled={!hasPrevious} onClick={onPrevious}>
        <ChevronLeftIcon />
        Previous Question
      </PagerLink>
      <PagerLink type='button' align='end' on={hasNext} disabled={!hasNext} onClick={onNext}>
        Next Question
        <ChevronRightIcon />
      </PagerLink>
    </FooterBar>
  );
}
